/**
 * FlipAgentCard - Interactive Agent Card with 3D Flip Animation
 * 
 * A sophisticated agent card that flips on contact button click to reveal
 * communication options: Call, Email, and Schedule a Meeting.
 * 
 * Architecture:
 * - The 3D flip animation is purely visual (CSS transforms)
 * - Interactive elements on the "back" are rendered in a separate overlay layer
 *   OUTSIDE the 3D transform context, ensuring reliable click detection
 * - This solves browser issues with pointer events in 3D-transformed elements
 */

'use client';

import Image from 'next/image';
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, Calendar, ArrowLeft } from 'lucide-react';
import type { Agent } from './AgentsSection';
import { AgentMeetingModal } from './AgentMeetingModal';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface FlipAgentCardProps {
  agent: Agent;
  index: number;
  organisationId: string;
  messages?: {
    contact?: string;
    call?: string;
    email?: string;
    scheduleMeeting?: string;
    properties?: string;
    backToProfile?: string;
  };
}

/**
 * FlipAgentCard Component
 * 
 * Uses a dual-layer approach:
 * 1. Visual layer: 3D flip animation for visual effect
 * 2. Interactive layer: Normal 2D elements for reliable click handling
 */
export function FlipAgentCard({
  agent,
  index,
  organisationId,
  messages = {},
}: FlipAgentCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  
  const {
    contact = 'Contact',
    call = 'Call',
    email = 'Email',
    scheduleMeeting = 'Schedule Meeting',
    properties = 'Properties',
    backToProfile = 'Back to Profile',
  } = messages;

  // Handlers - no need for stopPropagation since we use separate layers
  const handleContactClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFlipped(true);
  }, []);

  const handleBackClick = useCallback(() => {
    setIsFlipped(false);
  }, []);

  const handleCallClick = useCallback(() => {
    if (agent.phone) {
      analyticsEvents.clickToCall(agent.phone, `agent_${agent.name}`);
      window.location.href = `tel:${agent.phone.replace(/\s/g, '')}`;
    }
  }, [agent.phone, agent.name]);

  const handleEmailClick = useCallback(() => {
    if (agent.email) {
      analyticsEvents.clickToEmail(agent.email, `agent_${agent.name}`);
      const subject = encodeURIComponent(`Inquiry - ${agent.name}`);
      const body = encodeURIComponent(
        `Hello ${agent.name.split(' ')[0]},\n\nI would like to discuss a property with you.\n\nBest regards`
      );
      window.location.href = `mailto:${agent.email}?subject=${subject}&body=${body}`;
    }
  }, [agent.email, agent.name]);

  const handleScheduleClick = useCallback(() => {
    analyticsEvents.scheduleMeetingIntent(agent.name, 'agent_card');
    setShowMeetingModal(true);
  }, [agent.name]);

  return (
    <>
      {/* Card Container — shorter on mobile; fixed height keeps grid aligned */}
      <div className="relative h-[min(520px,78vh)] min-h-[400px] sm:h-[480px] lg:h-[500px]">
        
        {/* ============================================= */}
        {/* LAYER 1: 3D Flip Animation (Visual Only)     */}
        {/* ============================================= */}
        <div 
          className="absolute inset-0"
          style={{ perspective: '1500px' }}
        >
          <motion.div
            className="relative w-full h-full"
            animate={{ rotateY: isFlipped ? 180 : 0 }}
            transition={{ 
              duration: 0.6, 
              ease: [0.23, 1, 0.32, 1]
            }}
            style={{ 
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Front Face - Visual */}
            <div 
              className="absolute inset-0"
              style={{ 
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-50 to-gray-100 h-full shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
                {/* Large Name Typography - Background */}
                <div className="absolute inset-0 flex items-center justify-center overflow-hidden px-4">
                  <h3 
                    className="text-[100px] sm:text-[120px] md:text-[140px] lg:text-[160px] xl:text-[180px] font-black text-gray-900/4 leading-none tracking-tighter select-none pointer-events-none text-center font-inter"
                  >
                    {agent.name}
                  </h3>
                </div>

                {/* Agent Image */}
                <div className="absolute bottom-0 left-0 right-0 h-[85%]">
                  <div className="relative w-full h-full">
                    <Image
                      src={agent.image}
                      alt={agent.name}
                      fill
                      className="object-contain object-bottom"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw"
                      priority={index === 0}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Back Face - Visual */}
            <div 
              className="absolute inset-0"
              style={{ 
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <div className="relative overflow-hidden rounded-3xl h-full shadow-[0_8px_40px_rgba(0,0,0,0.15)]">
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
                
                {/* Decorative Elements */}
                <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-gradient-to-br from-violet-500/20 to-transparent rounded-full blur-3xl" />
                <div className="absolute -top-20 -left-20 w-48 h-48 bg-gradient-to-br from-blue-500/20 to-transparent rounded-full blur-3xl" />
                
                {/* Subtle Pattern Overlay */}
                <div 
                  className="absolute inset-0 opacity-5"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* ============================================= */}
        {/* LAYER 2: Interactive Elements (2D, Reliable) */}
        {/* ============================================= */}
        
        {/* Front Interactive Layer - Visible when NOT flipped */}
        <AnimatePresence>
          {!isFlipped && (
            <motion.div
              className="absolute inset-0 z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Card hover area — touch: always show CTA; desktop: reveal on hover */}
              <div className="absolute inset-0 rounded-3xl group">
                {/* Bottom gradient for name legibility (always on touch, hover on pointer devices) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-500 rounded-3xl pointer-events-none" />
                
                {/* Agent Info - Overlaid on image */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8 transform translate-y-0 [@media(hover:hover)]:translate-y-6 [@media(hover:hover)]:group-hover:translate-y-0 transition-transform duration-500">
                  <h4 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1 font-inter" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)' }}>
                    {agent.name}
                  </h4>
                  
                  {agent.title && (
                    <p className="text-sm font-medium text-white/90 mb-2 sm:mb-3 font-inter" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)' }}>
                      {agent.title}
                    </p>
                  )}

                  {agent.properties && (
                    <div className="flex items-center gap-4 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-500 mb-2 sm:mb-3">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <span className="text-sm font-semibold text-white/90 drop-shadow font-inter">
                          {agent.properties} {properties}
                        </span>
                      </div>
                    </div>
                  )}

                  {agent.bio && (
                    <p className="hidden sm:block text-sm text-white/90 leading-relaxed drop-shadow-lg opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-500 line-clamp-2 font-inter">
                      {agent.bio}
                    </p>
                  )}

                  {/* Mobile / touch: primary CTA with agent details */}
                  <button
                    type="button"
                    onClick={handleContactClick}
                    aria-label={`${contact} ${agent.name}`}
                    className="mt-4 w-full min-h-[44px] px-5 py-2.5 bg-white text-gray-900 rounded-full text-sm font-semibold shadow-lg active:scale-[0.98] transition-transform duration-200 font-inter touch-manipulation [@media(hover:hover)]:hidden"
                  >
                    {contact}
                  </button>
                </div>

                {/* Desktop: contact pill on hover */}
                <div className="absolute top-4 right-4 hidden [@media(hover:hover)]:block opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-500 z-20">
                  <button
                    type="button"
                    onClick={handleContactClick}
                    aria-label={`${contact} ${agent.name}`}
                    className="min-h-[44px] px-4 py-2 bg-white/95 backdrop-blur-md rounded-full text-sm font-semibold text-gray-900 shadow-lg hover:bg-white hover:scale-105 transition-all duration-200 cursor-pointer font-inter"
                  >
                    {contact}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Back Interactive Layer - Visible when flipped */}
        <AnimatePresence>
          {isFlipped && (
            <motion.div
              className="absolute inset-0 z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              {/* Back Button */}
              <button
                type="button"
                onClick={handleBackClick}
                aria-label={backToProfile}
                className="absolute top-3 left-3 sm:top-4 sm:left-4 z-30 flex items-center gap-2 min-h-[44px] max-w-[calc(100%-4.5rem)] px-3 py-2 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium text-white/90 hover:bg-white/20 active:bg-white/25 transition-all duration-200 cursor-pointer font-inter touch-manipulation"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" aria-hidden />
                <span className="truncate">{backToProfile}</span>
              </button>

              {/* Agent Mini Profile */}
              <div className="absolute top-3 right-3 sm:top-6 sm:right-6">
                <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden ring-2 ring-white/20 shadow-xl">
                  <Image
                    src={agent.image}
                    alt={agent.name}
                    fill
                    sizes="64px"
                    className="object-cover object-top"
                  />
                </div>
              </div>

              {/* Content — scrollable on small screens when all actions are shown */}
              <div className="absolute inset-0 flex flex-col justify-center items-center px-4 pt-16 pb-6 sm:px-8 sm:py-12 overflow-y-auto overscroll-contain">
                {/* Agent Name */}
                <h4 
                  className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 text-center font-inter"
                >
                  {agent.name}
                </h4>
                
                {agent.title && (
                  <p 
                    className="text-sm font-medium text-white/70 mb-5 sm:mb-8 text-center font-inter"
                  >
                    {agent.title}
                  </p>
                )}

                {/* Contact Options */}
                <div className="w-full max-w-xs space-y-2.5 sm:space-y-3">
                  {/* Call Button */}
                  {agent.phone && (
                    <button
                      type="button"
                      onClick={handleCallClick}
                      className="w-full flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-5 sm:py-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-2xl text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer touch-manipulation"
                    >
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white font-inter">
                          {call}
                        </p>
                        <p className="text-xs text-white/60 truncate font-inter">
                          {agent.phone}
                        </p>
                      </div>
                    </button>
                  )}

                  {/* Email Button */}
                  {agent.email && (
                    <button
                      type="button"
                      onClick={handleEmailClick}
                      className="w-full flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-5 sm:py-4 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-2xl text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer touch-manipulation"
                    >
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white font-inter">
                          {email}
                        </p>
                        <p className="text-xs text-white/60 truncate font-inter">
                          {agent.email}
                        </p>
                      </div>
                    </button>
                  )}

                  {/* Schedule Meeting Button */}
                  <button
                    type="button"
                    onClick={handleScheduleClick}
                    className="w-full flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-5 sm:py-4 bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 rounded-2xl text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer touch-manipulation"
                  >
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-violet-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white font-inter">
                        {scheduleMeeting}
                      </p>
                      <p className="text-xs text-white/60 font-inter">
                        Book a time that works for you
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Meeting Modal - Rendered at root level for proper stacking */}
      <AnimatePresence>
        {showMeetingModal && (
          <AgentMeetingModal
            agent={agent}
            organisationId={organisationId}
            onClose={() => setShowMeetingModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default FlipAgentCard;
